import React from 'react';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { DeactivateUserButtonValue } from './deactivate-user-button';
import { messages } from './messages';
import userDeactivationModalStyles from './user-deactivation-modal.module.css';
import { UserDateFormat } from '../../../components/user-date-format/index';

/**
 * Reusable component for displaying users with deactivation actions
 * @param {object} props
 * @param {number} props.maxAllowedUsers - Maximum allowed active users
 * @param {object[]} props.users - Array of user objects
 * @param {(users: object[]) => void} props.setUsers - State setter for users array
 * @param {string} [props.deactivateTheme] - Optional theme for deactivate button
 * @returns {React.ReactElement}
 */
export const UserDeactivationTable = ({ maxAllowedUsers, users, setUsers, deactivateTheme }) => {
  const numberOfActiveUsers = users.filter((user) => user.isActive).length;
  const showExclamationIcon = numberOfActiveUsers > maxAllowedUsers;

  return (
    <>
      <div className={userDeactivationModalStyles['user-counts-container']}>
        <div>
          <Text formattedMessageValues={{ maxAllowedUsers }} textType={TextType.Label} value={messages.maxAllowedUsers} />
        </div>
        <div>
          <Text
            formattedMessageValues={{
              currentActiveUsers: numberOfActiveUsers,
            }}
            textType={TextType.Label}
            value={messages.currentActiveUsers}
          />
          {showExclamationIcon ? (
            <FontAwesomeIcon
              className={userDeactivationModalStyles['error-icon']}
              data-testid='exclamation-icon'
              icon={faExclamationCircle}
            />
          ) : (
            <FontAwesomeIcon className={userDeactivationModalStyles['success-icon']} data-testid='check-icon' icon={faCheckCircle} />
          )}
        </div>
      </div>
      <table className={userDeactivationModalStyles['active-user-grid']}>
        <thead>
          <tr>
            <th>
              <Text textType={TextType.Label} value={messages.userName} />
            </th>
            <th>
              <Text textType={TextType.Label} value={messages.userLastLoginDate} />
            </th>
            <th>
              <Text textType={TextType.Label} value={messages.statusColumnHeader} />
            </th>
            <th>
              <Text textType={TextType.Label} value={messages.actions} />
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={`${user.userId}`} className={user.isActive ? '' : userDeactivationModalStyles['row-deactivated']}>
              <td key={`${user.userId}-name`}>
                <Text textType={TextType.Paragraph} value={user.name} />
              </td>
              <td key={`${user.userId}-login`}>
                <Text textType={TextType.Paragraph} value={<UserDateFormat value={user.lastLoginDate} />} />
              </td>
              <td key={`${user.userId}-active`}>
                <Text textType={TextType.Paragraph} value={user.isActive ? messages.activeStatus : messages.deactivated} />
              </td>
              <td key={`${user.userId}-actions`}>
                <DeactivateUserButtonValue
                  className={userDeactivationModalStyles.button}
                  deactivateTheme={deactivateTheme ?? THEME_CONSTANTS.secondary}
                  setUsers={setUsers}
                  user={user}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
